import { FullProg_v4 } from '~/providers/types';
import { DamEcoSimplifiedConfig, DamEcoSimplifiedConfigParams, DamEcoSimplifiedProgrammingTitles } from '~/providers/types/api-private';
import { STAGE_DELAY_MAX, STAGE_DELAY_MIN } from './constants';
import {
  DamEcoSimplifiedFormValues, DamEcoSimplifiedValidationKey, DamReferenceDutOption, DamSelfReferenceEnvironmentOption,
} from './types';

const INTEGER_PATTERN = /^\d+$/;

/** Converte o texto digitado em número (aceita vírgula decimal); vazio vira null e texto inválido vira NaN. */
export function parseFormNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return Number(trimmed.replace(',', '.'));
}

function numberToFormText(value: number | null): string {
  return value == null ? '' : String(value);
}

export function validateStageDelay(value: string): DamEcoSimplifiedValidationKey | null {
  const trimmed = value.trim();
  if (!INTEGER_PATTERN.test(trimmed)) return 'ecoSimplificadoAtrasoForaDaFaixa';
  const parsed = Number(trimmed);
  if (parsed < STAGE_DELAY_MIN || parsed > STAGE_DELAY_MAX) return 'ecoSimplificadoAtrasoForaDaFaixa';
  return null;
}

export function validateRequiredNumber(value: string): DamEcoSimplifiedValidationKey | null {
  const parsed = parseFormNumber(value);
  if (parsed == null) return 'ecoSimplificadoValorObrigatorio';
  if (!Number.isFinite(parsed)) return 'ecoSimplificadoValorNumerico';
  return null;
}

export function validatePositiveNumber(value: string): DamEcoSimplifiedValidationKey | null {
  const requiredError = validateRequiredNumber(value);
  if (requiredError) return requiredError;
  return (parseFormNumber(value) ?? 0) > 0 ? null : 'ecoSimplificadoValorMaiorQueZero';
}

export function validateOptionalNumber(value: string): DamEcoSimplifiedValidationKey | null {
  const parsed = parseFormNumber(value);
  if (parsed == null || Number.isFinite(parsed)) return null;
  return 'ecoSimplificadoValorNumerico';
}

export function validateOptionalMinutes(value: string): DamEcoSimplifiedValidationKey | null {
  const trimmed = value.trim();
  if (!trimmed || INTEGER_PATTERN.test(trimmed)) return null;
  return 'ecoSimplificadoMinutosInvalidos';
}

export function toDamEcoSimplifiedFormValues(
  config: DamEcoSimplifiedConfig,
  programming: FullProg_v4,
  programmingTitles: DamEcoSimplifiedProgrammingTitles,
): DamEcoSimplifiedFormValues {
  return {
    enableEco: config.ENABLE_ECO,
    enableEcoLocal: config.ENABLE_ECO_LOCAL,
    setpoint: numberToFormText(config.SETPOINT),
    upperHysteresis: numberToFormText(config.UPPER_HYSTERESIS),
    lowerHysteresis: numberToFormText(config.LOWER_HYSTERESIS),
    lti: numberToFormText(config.LTI),
    stageAddDelay: String(config.ECO_STAGE_ADD_DELAY),
    stageReduceDelay: String(config.ECO_STAGE_REDUCE_DELAY),
    primaryCondenser: config.ECO_PRIMARY_CONDENSER,
    ventStart: numberToFormText(config.ECO_VENT_START_TIME),
    ventEnd: numberToFormText(config.ECO_VENT_END_TIME),
    selfReference: config.SELF_REFERENCE,
    referenceDutId: config.REL_DUT_ID,
    referenceEnvironmentId: config.REL_ENVIRONMENT_ID,
    programming,
    programmingTitles,
  };
}

/** Campos que só vão no salvamento quando foram alterados, para não regravá-los sem necessidade. */
export interface DamEcoSimplifiedChangedFields {
  programming: boolean;
  referenceDut: boolean;
  programmingTitles: boolean;
  selfReference: boolean;
  referenceEnvironment: boolean;
}

/**
 * Ambiente monitorado pelo próprio DAM, como o `/dam/set-dam-info` do modo atual: com o sensor próprio,
 * o ambiente escolhido; com o DUT de referência, null. Vai só quando algo da referência mudou.
 */
function referenceEnvironmentParam(values: DamEcoSimplifiedFormValues, changed: DamEcoSimplifiedChangedFields): Pick<DamEcoSimplifiedConfigParams, 'REL_ENVIRONMENT_ID'> {
  const changedToDut = !values.selfReference && changed.referenceDut;
  if (!changed.referenceEnvironment && !changed.selfReference && !changedToDut) return {};
  return { REL_ENVIRONMENT_ID: values.selfReference ? values.referenceEnvironmentId : null };
}

/** Monta o corpo do salvamento a partir de valores já validados pelo formulário. */
export function toDamEcoSimplifiedSaveParams(damId: string, values: DamEcoSimplifiedFormValues, changed: DamEcoSimplifiedChangedFields): DamEcoSimplifiedConfigParams {
  return {
    DAM_ID: damId,
    ENABLE_ECO: values.enableEco,
    ENABLE_ECO_LOCAL: values.enableEcoLocal,
    SETPOINT: parseFormNumber(values.setpoint) ?? 0,
    UPPER_HYSTERESIS: parseFormNumber(values.upperHysteresis) ?? 0,
    LOWER_HYSTERESIS: parseFormNumber(values.lowerHysteresis) ?? 0,
    LTI: parseFormNumber(values.lti),
    ECO_STAGE_ADD_DELAY: Number(values.stageAddDelay.trim()),
    ECO_STAGE_REDUCE_DELAY: Number(values.stageReduceDelay.trim()),
    ECO_PRIMARY_CONDENSER: values.primaryCondenser,
    ECO_VENT_START_TIME: parseFormNumber(values.ventStart),
    ECO_VENT_END_TIME: parseFormNumber(values.ventEnd),
    SELF_REFERENCE: values.selfReference,
    ...(changed.referenceDut ? { REL_DUT_ID: values.referenceDutId } : {}),
    ...referenceEnvironmentParam(values, changed),
    ...(changed.programming ? { PROGRAMMING: values.programming } : {}),
    ...(changed.programmingTitles ? { PROGRAMMING_TITLES: values.programmingTitles } : {}),
  };
}

export interface MonitoredEnvironment {
  roomName: string | null;
  roomTypeName: string | null;
  limits: { min: number | null; max: number | null };
}

/**
 * Ambiente, tipo e limites exibidos: do DUT escolhido ou, com o sensor próprio do DAM, do ambiente que
 * ele monitora (sem limites próprios do ambiente, valem os do DAM).
 */
export function resolveMonitoredEnvironment(params: {
  selfReference: boolean;
  reference: { dutId: string | null; environmentId: number | null };
  options: { duts: DamReferenceDutOption[]; environments: DamSelfReferenceEnvironmentOption[] };
  damLimits: MonitoredEnvironment['limits'];
}): MonitoredEnvironment {
  if (params.selfReference) {
    const environment = params.options.environments.find((item) => item.ENVIRONMENT_ID === params.reference.environmentId);
    return {
      roomName: environment?.ROOM_NAME ?? null,
      roomTypeName: environment?.RTYPE_NAME ?? null,
      limits: { min: environment?.TUSEMIN ?? params.damLimits.min, max: environment?.TUSEMAX ?? params.damLimits.max },
    };
  }
  const option = params.options.duts.find((item) => item.DEV_ID === params.reference.dutId);
  return {
    roomName: option?.ROOM_NAME ?? null,
    roomTypeName: option?.RTYPE_NAME ?? null,
    limits: { min: option?.TUSEMIN ?? null, max: option?.TUSEMAX ?? null },
  };
}

/** Nome do DUT no seletor de ambiente: o ambiente em que está ou, sem ambiente, o código. */
export function getReferenceDutLabel(option: DamReferenceDutOption): string {
  // Nome vazio também cai no código, por isso não é `??`.
  return option.ROOM_NAME ? option.ROOM_NAME : option.DEV_ID;
}

/** "20 °C – 25 °C"; sem nenhum limite, "-". */
export function formatTemperatureRange(limits: MonitoredEnvironment['limits']): string {
  if (limits.min == null && limits.max == null) return '-';
  const format = (value: number | null): string => (value == null ? '-' : `${value} °C`);
  return `${format(limits.min)} – ${format(limits.max)}`;
}

/** Ambiente da unidade como vem de `/environments/get-environment-list` (com `CHECK_DAMS_MONITORING`). */
export interface UnitEnvironment {
  ENVIRONMENT_ID: number;
  ENVIRONMENT_NAME: string | null;
  RTYPE_NAME: string | null;
  DUT_CODE: string | null;
  DAM_MONITORING_DEVICE_ID: number | null;
  MINIMUM_TEMPERATURE: number | null;
  MAXIMUM_TEMPERATURE: number | null;
}

/**
 * Ambientes que o próprio DAM pode monitorar, com o mesmo filtro da tela do modo atual: sem DUT e sem
 * outro DAM monitorando (o que já é monitorado por este DAM continua na lista).
 */
export function toSelfReferenceEnvironmentOptions(environments: UnitEnvironment[], damDeviceId: number | null): DamSelfReferenceEnvironmentOption[] {
  return environments
    .filter((environment) => !environment.DUT_CODE && (!environment.DAM_MONITORING_DEVICE_ID || environment.DAM_MONITORING_DEVICE_ID === damDeviceId))
    .map((environment) => ({
      ENVIRONMENT_ID: environment.ENVIRONMENT_ID,
      ROOM_NAME: environment.ENVIRONMENT_NAME ?? null,
      RTYPE_NAME: environment.RTYPE_NAME ?? null,
      TUSEMIN: environment.MINIMUM_TEMPERATURE ?? null,
      TUSEMAX: environment.MAXIMUM_TEMPERATURE ?? null,
    }));
}

export interface MonitoredEnvironmentSelectOption {
  value: string;
  label: string;
}

/**
 * Opções do select AMBIENTE: com o sensor próprio, só ambientes (e o ambiente gravado que não está na
 * lista, para não sumir); com o DUT de referência, só DUTs.
 */
export function buildMonitoredEnvironmentOptions(params: {
  selfReference: boolean;
  options: { duts: DamReferenceDutOption[]; environments: DamSelfReferenceEnvironmentOption[] };
  currentEnvironmentId: number | null;
}): MonitoredEnvironmentSelectOption[] {
  if (!params.selfReference) {
    return params.options.duts.map((option) => ({ value: option.DEV_ID, label: getReferenceDutLabel(option) }));
  }
  const environments = params.options.environments.map((option) => ({
    value: String(option.ENVIRONMENT_ID), label: option.ROOM_NAME ? option.ROOM_NAME : String(option.ENVIRONMENT_ID),
  }));
  const { currentEnvironmentId } = params;
  const isSavedOutsideList = currentEnvironmentId != null
    && !params.options.environments.some((option) => option.ENVIRONMENT_ID === currentEnvironmentId);
  return isSavedOutsideList ? [{ value: String(currentEnvironmentId), label: String(currentEnvironmentId) }, ...environments] : environments;
}
